/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react";
import { useSignalR } from "./SignalRProvider";
import { useHotelGroupManagement } from "./useHotelGroupManagement";
import * as signalR from "@microsoft/signalr";
import { logger } from "../../utils/logger";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventHandler = (...args: any[]) => void;

interface HotelHubContextValue {
  on: (eventName: string, handler: EventHandler) => void;
  off: (eventName: string, handler: EventHandler) => void;
  invoke: (methodName: string, ...args: unknown[]) => Promise<void>;
  isConnected: boolean;
  isReconnecting: boolean;
  setOnReconnectedCallback: (callback: (() => void) | null) => void;
}

const HotelHubContext = createContext<HotelHubContextValue | undefined>(
  undefined,
);

export const useHotelHub = (): HotelHubContextValue => {
  const context = useContext(HotelHubContext);
  if (!context) {
    throw new Error("useHotelHub must be used within a HotelHubProvider");
  }
  return context;
};

interface HotelHubProviderProps {
  children: React.ReactNode;
  hotelId?: string;
}

export const HotelHubProvider: React.FC<HotelHubProviderProps> = ({
  children,
  hotelId,
}) => {
  const { getConnection } = useSignalR();
  const handlersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
  const hubUrl = "/hub";
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const wasConnectedRef = useRef<boolean>(false);
  const onReconnectedCallbackRef = useRef<(() => void) | null>(null);
  const registeredHandlersRef = useRef<Set<EventHandler>>(new Set());

  useHotelGroupManagement(hotelId);

  const getHubConnection = useCallback((): signalR.HubConnection | null => {
    const connectionInfo = getConnection(hubUrl);
    return connectionInfo?.connection || null;
  }, [getConnection]);

  const on = useCallback(
    (eventName: string, handler: EventHandler) => {
      if (!handlersRef.current.has(eventName)) {
        handlersRef.current.set(eventName, new Set());
      }
      handlersRef.current.get(eventName)!.add(handler);

      const connection = getHubConnection();
      if (connection) {
        connection.on(eventName, handler);
      }
    },
    [getHubConnection],
  );

  const off = useCallback(
    (eventName: string, handler: EventHandler) => {
      const handlers = handlersRef.current.get(eventName);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          handlersRef.current.delete(eventName);
        }
      }

      const connection = getHubConnection();
      if (connection) {
        connection.off(eventName, handler);
      }
    },
    [getHubConnection],
  );

  const invoke = useCallback(
    async (methodName: string, ...args: unknown[]): Promise<void> => {
      const connection = getHubConnection();
      if (
        !connection ||
        connection.state !== signalR.HubConnectionState.Connected
      ) {
        throw new Error("HotelHub is not connected");
      }

      try {
        await connection.invoke(methodName, ...args);
      } catch (error) {
        logger.error(`Failed to invoke ${methodName}:`, error);
        throw error;
      }
    },
    [getHubConnection],
  );

  // Register default no-op handlers for high-frequency chat events that are optional.
  // This prevents "No client method found" warnings when not on the chat page.
  useEffect(() => {
    const connection = getHubConnection();
    if (!connection) return;

     
    const noOpTypingIndicator = (
      _roomId: string,
      _isTyping: boolean,
      _isStaffMessage: boolean,
    ) => {};
     
    const noOpReadReceipt = (_roomId: string) => {};
     
    const noOpMessageAttachment = (
      _roomId: string,
      _attachmentInfo: unknown,
    ) => {};

    connection.on("ReceiveTypingIndicator", noOpTypingIndicator);
    connection.on("ReceiveReadReceipt", noOpReadReceipt);
    connection.on("ReceiveMessageAttachment", noOpMessageAttachment);

    return () => {
      connection.off("ReceiveTypingIndicator", noOpTypingIndicator);
      connection.off("ReceiveReadReceipt", noOpReadReceipt);
      connection.off("ReceiveMessageAttachment", noOpMessageAttachment);
    };
  }, [getHubConnection]);

  // Re-register handlers when connection changes
  useEffect(() => {
    const connection = getHubConnection();
    if (!connection) return;

    const currentHandlers = handlersRef.current;
    const alreadyRegistered = registeredHandlersRef.current;

    currentHandlers.forEach((handlers, eventName) => {
      handlers.forEach((handler) => {
        if (!alreadyRegistered.has(handler)) {
          connection.on(eventName, handler);
          alreadyRegistered.add(handler);
        }
      });
    });

    return () => {
      currentHandlers.forEach((handlers, eventName) => {
        handlers.forEach((handler) => {
          connection.off(eventName, handler);
          alreadyRegistered.delete(handler);
        });
      });
    };
  }, [getHubConnection]);

  const connectionInfo = getConnection(hubUrl);
  const isConnected = connectionInfo?.isConnected || false;

  // Track reconnection state by monitoring connection changes
  useEffect(() => {
    const connection = getHubConnection();
    if (!connection) return;

    const handleReconnecting = () => {
      logger.log("HotelHubProvider - Connection reconnecting");
      setIsReconnecting(true);
    };

    const handleReconnected = () => {
      logger.log("HotelHubProvider - Connection reconnected");
      setIsReconnecting(false);

      if (onReconnectedCallbackRef.current) {
        onReconnectedCallbackRef.current();
      }
    };

    connection.onreconnecting(handleReconnecting);
    connection.onreconnected(handleReconnected);

    if (isConnected && !wasConnectedRef.current) {
      wasConnectedRef.current = true;
    } else if (!isConnected && wasConnectedRef.current) {
      setIsReconnecting(true);
    }

    if (isConnected) {
      setIsReconnecting(false);
      wasConnectedRef.current = true;
    }
  }, [getHubConnection, isConnected]);

  const setOnReconnectedCallback = useCallback(
    (callback: (() => void) | null) => {
      onReconnectedCallbackRef.current = callback;
    },
    [],
  );

  const contextValue = useMemo<HotelHubContextValue>(
    () => ({
      on,
      off,
      invoke,
      isConnected,
      isReconnecting,
      setOnReconnectedCallback,
    }),
    [on, off, invoke, isConnected, isReconnecting, setOnReconnectedCallback],
  );

  return (
    <HotelHubContext.Provider value={contextValue}>
      {children}
    </HotelHubContext.Provider>
  );
};
